/* New fan flow — the ordered steps the AI follows to open a chat with a new fan.
   Stored on the account as `start_with_new_fan_setup`: [{heading, description,
   message_example}], where `description` is the private AI coaching note. */

import type { Account } from '../../../../api/client';

export interface Step {
  id: string;
  name: string;
  message: string;
  coaching: string;
  open: boolean;
}

export interface StoredStep {
  heading: string;
  description: string;
  message_example: string;
}

// Older rows were written with this key for the example message.
const MESSAGE_EXAMPLE_ALT_KEY = 'message_example (can be split into separate messages if needed)';

let seq = 0;
export const stepId = () => `nf-${Date.now()}-${(seq += 1)}`;

const text = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));

export function stepsOf(stored: Account['start_with_new_fan_setup']): Step[] {
  if (!Array.isArray(stored)) return [];
  return stored.map((raw) => {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return {
      id: stepId(),
      name: text(o.heading),
      message: text(o.message_example) || text(o[MESSAGE_EXAMPLE_ALT_KEY]),
      coaching: text(o.description),
      open: false,
    };
  });
}

/** What gets saved: trimmed, and steps with nothing in them are left out. */
export function payloadOf(steps: Step[]): StoredStep[] {
  return steps
    .map((s) => ({ heading: s.name.trim(), description: s.coaching.trim(), message_example: s.message.trim() }))
    .filter((s) => s.heading || s.description || s.message_example);
}

export const isBlank = (s: Step) => !s.message.trim() && !s.coaching.trim();
